/**
 * ChannelRoiDetailPage
 * Issue #137 — 渠道详情页
 *
 * 包含：
 * - 基本信息 + 合作状态转换
 * - 联系人列表
 * - 业务拓展活动列表 + 成本汇总
 * - 效果评价列表
 * - 分成明细列表
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation } from 'wouter';
import { toast } from 'sonner';
import * as Dialog from '@radix-ui/react-dialog';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import AccessDenied from '@/components/AccessDenied';
import { useChannelPartnerDetail } from '@/hooks/useCrm';
import type { ChannelPartnerDto } from '@/hooks/useCrm';
import {
  COOPERATION_STATUS_LABELS,
  ACTIVITY_STATUS_LABELS,
  EVALUATION_STATUS_LABELS,
  COMMISSION_STATUS_LABELS,
  type ChannelPartnerCooperationStatus,
  type ActivityStatus,
  type EvaluationStatus,
  type CommissionStatus,
  type ChannelContactDto,
  type BusinessDevActivityDto,
  type ChannelCostDto,
  type ChannelEffectEvaluationDto,
  type ChannelCommissionItemDto,
  type CreateBusinessDevActivityDto,
  type CreateChannelCostDto,
  type CreateChannelEffectEvaluationDto,
  type TransitionCooperationStatusDto,
  useTransitionCooperationStatus,
  useChannelContactList,
  useChannelContactCreate,
  useChannelContactDelete,
  useActivityList,
  useActivityCreate,
  useChannelCostList,
  useChannelCostCreate,
  useEvaluationList,
  useEvaluationCreate,
  useCommissionItemList,
} from '@/hooks/useChannelRoi';
import {
  CHANNEL_ROI_PERMISSIONS,
  hasPermission,
} from '@/lib/channelRoiPermissions';
import { formatMoney, formatRatio, sumDecimalStrings } from '@/lib/decimalUtils';

// ─── Cooperation Status Transition Dialog ─────────────────────────────────────

const COOP_EVENTS: Array<{
  event: string;
  label: string;
  fromStatuses: ChannelPartnerCooperationStatus[];
}> = [
  { event: 'START_NEGOTIATION', label: '开始洽谈', fromStatuses: ['prospecting'] },
  { event: 'BACK_TO_PROSPECT', label: '退回潜在', fromStatuses: ['negotiating'] },
  { event: 'SIGN_CONTRACT', label: '签署合同', fromStatuses: ['negotiating'] },
  { event: 'ACTIVATE', label: '激活合作', fromStatuses: ['contracted', 'suspended_cooperation'] },
  { event: 'SUSPEND', label: '暂停合作', fromStatuses: ['active_cooperation'] },
  {
    event: 'TERMINATE',
    label: '终止合作',
    fromStatuses: ['active_cooperation', 'suspended_cooperation', 'contracted'],
  },
  { event: 'ARCHIVE', label: '归档', fromStatuses: ['terminated'] },
];

function CoopStatusBadge({ status }: { status?: string | null }) {
  const label =
    status && status in COOPERATION_STATUS_LABELS
      ? COOPERATION_STATUS_LABELS[status as ChannelPartnerCooperationStatus]
      : status ?? '—';
  const colorMap: Readonly<Record<string, string>> = {
    prospecting: 'bg-gray-100 text-gray-700',
    negotiating: 'bg-blue-100 text-blue-700',
    contracted: 'bg-indigo-100 text-indigo-700',
    active_cooperation: 'bg-green-100 text-green-700',
    suspended_cooperation: 'bg-yellow-100 text-yellow-700',
    terminated: 'bg-red-100 text-red-700',
    archived: 'bg-gray-100 text-gray-500',
  };
  const cls = (status && colorMap[status]) ?? 'bg-gray-100 text-gray-700';
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>{label}</span>;
}

interface CoopTransitionDialogProps {
  partner: ChannelPartnerDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTransitioned: () => void;
}

function CoopTransitionDialog({
  partner,
  open,
  onOpenChange,
  onTransitioned,
}: CoopTransitionDialogProps) {
  const { loading, error, transition } = useTransitionCooperationStatus();
  const [selectedEvent, setSelectedEvent] = useState<
    TransitionCooperationStatusDto['event'] | ''
  >('' as TransitionCooperationStatusDto['event'] | '');
  const [notes, setNotes] = useState('');

  const currentStatus = (
    partner.cooperationStatus ?? 'prospecting'
  ) as ChannelPartnerCooperationStatus;

  const availableEvents = COOP_EVENTS.filter((e) => e.fromStatuses.includes(currentStatus));

  async function handleSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    if (!selectedEvent) {
      toast.error('请选择转换事件');
      return;
    }
    const ok = await transition(partner.id, {
      event: selectedEvent,
      notes: notes || undefined,
      expectedVersion: partner.version,
    });
    if (ok) {
      toast.success('合作状态已更新');
      onOpenChange(false);
      onTransitioned();
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg bg-background p-6 shadow-lg">
          <Dialog.Title className="mb-4 text-lg font-semibold">合作状态转换</Dialog.Title>
          <form
            onSubmit={(e) => {
              void handleSubmit(e);
            }}
            className="space-y-4"
          >
            <div className="space-y-1">
              <Label>当前状态</Label>
              <CoopStatusBadge status={currentStatus} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="coop-event">转换事件</Label>
              {availableEvents.length === 0 ? (
                <p className="text-sm text-muted-foreground">当前状态无可用转换</p>
              ) : (
                <select
                  id="coop-event"
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                  value={selectedEvent}
                  onChange={(e) =>
                    setSelectedEvent(
                      e.target.value as TransitionCooperationStatusDto['event'] | '',
                    )
                  }
                  required
                >
                  <option value="">请选择…</option>
                  {availableEvents.map((e) => (
                    <option key={e.event} value={e.event}>
                      {e.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="coop-notes">备注</Label>
              <Input
                id="coop-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="可选备注"
              />
            </div>
            {error && (
              <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error.message}
              </p>
            )}
            <div className="flex gap-3 pt-2">
              <Button
                type="submit"
                disabled={loading || availableEvents.length === 0}
              >
                {loading ? '处理中…' : '确认转换'}
              </Button>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                取消
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ─── Add Contact Dialog ───────────────────────────────────────────────────────

interface AddContactDialogProps {
  channelPartnerId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}

function AddContactDialog({
  channelPartnerId,
  open,
  onOpenChange,
  onCreated,
}: AddContactDialogProps) {
  const { loading, error, create } = useChannelContactCreate();
  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [isPrimary, setIsPrimary] = useState(false);

  async function handleSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    const result = await create({
      channelPartnerId,
      name,
      title: title || undefined,
      phone: phone || undefined,
      email: email || undefined,
      isPrimary,
    });
    if (result) {
      toast.success('联系人已添加');
      onOpenChange(false);
      onCreated();
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg bg-background p-6 shadow-lg">
          <Dialog.Title className="mb-4 text-lg font-semibold">添加联系人</Dialog.Title>
          <form
            onSubmit={(e) => {
              void handleSubmit(e);
            }}
            className="space-y-4"
          >
            <div className="space-y-1">
              <Label htmlFor="contact-name">姓名 *</Label>
              <Input
                id="contact-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="contact-title">职位</Label>
              <Input
                id="contact-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="contact-phone">手机号</Label>
              <Input
                id="contact-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="contact-email">邮箱</Label>
              <Input
                id="contact-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                id="contact-primary"
                type="checkbox"
                checked={isPrimary}
                onChange={(e) => setIsPrimary(e.target.checked)}
              />
              <Label htmlFor="contact-primary">设为主要联系人</Label>
            </div>
            {error && (
              <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error.message}
              </p>
            )}
            <div className="flex gap-3 pt-2">
              <Button type="submit" disabled={loading}>
                {loading ? '添加中…' : '添加'}
              </Button>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                取消
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ─── Add Activity Dialog ──────────────────────────────────────────────────────

interface AddActivityDialogProps {
  channelPartnerId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}

const ACTIVITY_TYPES = [
  { value: 'marketing_event', label: '营销活动' },
  { value: 'visit', label: '拜访' },
  { value: 'seminar', label: '研讨会' },
  { value: 'gift', label: '礼品' },
  { value: 'meal_entertainment', label: '餐饮招待' },
  { value: 'exhibition', label: '展览' },
  { value: 'online_campaign', label: '线上推广' },
  { value: 'other', label: '其他' },
] as const;

function AddActivityDialog({
  channelPartnerId,
  open,
  onOpenChange,
  onCreated,
}: AddActivityDialogProps) {
  const { loading, error, create } = useActivityCreate();
  const [name, setName] = useState('');
  const [activityType, setActivityType] = useState<
    CreateBusinessDevActivityDto['activityType']
  >('marketing_event' as CreateBusinessDevActivityDto['activityType']);
  const [startDate, setStartDate] = useState('');
  const [budgetAmount, setBudgetAmount] = useState('');
  const [description, setDescription] = useState('');

  async function handleSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    const result = await create({
      channelPartnerId,
      name,
      activityType,
      startDate,
      budgetAmount: budgetAmount || undefined,
      description: description || undefined,
    });
    if (result) {
      toast.success('活动已创建');
      onOpenChange(false);
      onCreated();
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg bg-background p-6 shadow-lg">
          <Dialog.Title className="mb-4 text-lg font-semibold">新建拓展活动</Dialog.Title>
          <form
            onSubmit={(e) => {
              void handleSubmit(e);
            }}
            className="space-y-4"
          >
            <div className="space-y-1">
              <Label htmlFor="act-name">活动名称 *</Label>
              <Input
                id="act-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="act-type">活动类型 *</Label>
              <select
                id="act-type"
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                value={activityType}
                onChange={(e) =>
                  setActivityType(
                    e.target.value as CreateBusinessDevActivityDto['activityType'],
                  )
                }
              >
                {ACTIVITY_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="act-start">开始日期 *</Label>
              <Input
                id="act-start"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="act-budget">预算金额（元）</Label>
              <Input
                id="act-budget"
                value={budgetAmount}
                onChange={(e) => setBudgetAmount(e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="act-desc">描述</Label>
              <textarea
                id="act-desc"
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            {error && (
              <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error.message}
              </p>
            )}
            <div className="flex gap-3 pt-2">
              <Button type="submit" disabled={loading}>
                {loading ? '创建中…' : '创建'}
              </Button>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                取消
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ─── Add Cost Dialog ──────────────────────────────────────────────────────────

interface AddCostDialogProps {
  channelPartnerId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}

const COST_TYPES = [
  { value: 'commission', label: '佣金' },
  { value: 'gift', label: '礼品' },
  { value: 'meal', label: '餐饮' },
  { value: 'event', label: '活动' },
  { value: 'transport', label: '交通' },
  { value: 'material', label: '物料' },
  { value: 'other', label: '其他' },
] as const;

function AddCostDialog({
  channelPartnerId,
  open,
  onOpenChange,
  onCreated,
}: AddCostDialogProps) {
  const { loading, error, create } = useChannelCostCreate();
  const [costType, setCostType] = useState<CreateChannelCostDto['costType']>(
    'commission' as CreateChannelCostDto['costType'],
  );
  const [amount, setAmount] = useState('');
  const [costDate, setCostDate] = useState('');
  const [description, setDescription] = useState('');

  async function handleSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    const result = await create({
      channelPartnerId,
      costType,
      amount,
      currency: 'CNY',
      costDate,
      description: description || undefined,
    });
    if (result) {
      toast.success('成本记录已添加');
      onOpenChange(false);
      onCreated();
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg bg-background p-6 shadow-lg">
          <Dialog.Title className="mb-4 text-lg font-semibold">添加成本记录</Dialog.Title>
          <form
            onSubmit={(e) => {
              void handleSubmit(e);
            }}
            className="space-y-4"
          >
            <div className="space-y-1">
              <Label htmlFor="cost-type">成本类型 *</Label>
              <select
                id="cost-type"
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                value={costType}
                onChange={(e) =>
                  setCostType(e.target.value as CreateChannelCostDto['costType'])
                }
              >
                {COST_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="cost-amount">金额（元）*</Label>
              <Input
                id="cost-amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                required
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="cost-date">发生日期 *</Label>
              <Input
                id="cost-date"
                type="date"
                value={costDate}
                onChange={(e) => setCostDate(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="cost-desc">描述</Label>
              <Input
                id="cost-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            {error && (
              <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error.message}
              </p>
            )}
            <div className="flex gap-3 pt-2">
              <Button type="submit" disabled={loading}>
                {loading ? '添加中…' : '添加'}
              </Button>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                取消
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ─── Add Evaluation Dialog ────────────────────────────────────────────────────

interface AddEvaluationDialogProps {
  channelPartnerId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}

function AddEvaluationDialog({
  channelPartnerId,
  open,
  onOpenChange,
  onCreated,
}: AddEvaluationDialogProps) {
  const { loading, error, create } = useEvaluationCreate();
  const [periodType, setPeriodType] = useState<
    CreateChannelEffectEvaluationDto['periodType']
  >('monthly' as CreateChannelEffectEvaluationDto['periodType']);
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [totalLeads, setTotalLeads] = useState('0');
  const [convertedLeads, setConvertedLeads] = useState('0');
  const [totalRevenue, setTotalRevenue] = useState('0');
  const [totalCost, setTotalCost] = useState('0');
  const [grossProfit, setGrossProfit] = useState('0');

  async function handleSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    const result = await create({
      channelPartnerId,
      periodType,
      periodStart,
      periodEnd,
      totalLeads: parseInt(totalLeads, 10),
      convertedLeads: parseInt(convertedLeads, 10),
      totalRevenue,
      totalCost,
      grossProfit,
      repurchaseCount: 0,
      complaintCount: 0,
      refundCount: 0,
    });
    if (result) {
      toast.success('效果评价已创建');
      onOpenChange(false);
      onCreated();
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 w-full max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-lg bg-background p-6 shadow-lg">
          <Dialog.Title className="mb-4 text-lg font-semibold">新建效果评价</Dialog.Title>
          <form
            onSubmit={(e) => {
              void handleSubmit(e);
            }}
            className="space-y-4"
          >
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="eval-period-type">周期类型</Label>
                <select
                  id="eval-period-type"
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                  value={periodType}
                  onChange={(e) =>
                    setPeriodType(
                      e.target.value as CreateChannelEffectEvaluationDto['periodType'],
                    )
                  }
                >
                  <option value="monthly">月度</option>
                  <option value="quarterly">季度</option>
                  <option value="yearly">年度</option>
                  <option value="custom">自定义</option>
                </select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="eval-start">开始日期 *</Label>
                <Input
                  id="eval-start"
                  type="date"
                  value={periodStart}
                  onChange={(e) => setPeriodStart(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="eval-end">结束日期 *</Label>
                <Input
                  id="eval-end"
                  type="date"
                  value={periodEnd}
                  onChange={(e) => setPeriodEnd(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="eval-leads">线索总数</Label>
                <Input
                  id="eval-leads"
                  type="number"
                  min="0"
                  value={totalLeads}
                  onChange={(e) => setTotalLeads(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="eval-converted">转化数</Label>
                <Input
                  id="eval-converted"
                  type="number"
                  min="0"
                  value={convertedLeads}
                  onChange={(e) => setConvertedLeads(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="eval-revenue">总收入（元）</Label>
                <Input
                  id="eval-revenue"
                  value={totalRevenue}
                  onChange={(e) => setTotalRevenue(e.target.value)}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="eval-cost">总成本（元）</Label>
                <Input
                  id="eval-cost"
                  value={totalCost}
                  onChange={(e) => setTotalCost(e.target.value)}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="eval-profit">毛利润（元）</Label>
                <Input
                  id="eval-profit"
                  value={grossProfit}
                  onChange={(e) => setGrossProfit(e.target.value)}
                  placeholder="0.00"
                />
              </div>
            </div>
            {error && (
              <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error.message}
              </p>
            )}
            <div className="flex gap-3 pt-2">
              <Button type="submit" disabled={loading}>
                {loading ? '创建中…' : '创建'}
              </Button>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                取消
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ChannelRoiDetailPage() {
  const params = useParams<{ partnerId: string }>();
  const partnerId = params.partnerId;
  const [, navigate] = useLocation();
  const { permissions } = useAuth();

  const { loading: partnerLoading, fetch: fetchPartner } = useChannelPartnerDetail();
  const { fetch: fetchContacts } = useChannelContactList();
  const { remove: removeContact } = useChannelContactDelete();
  const { fetch: fetchActivities } = useActivityList();
  const { fetch: fetchCosts } = useChannelCostList();
  const { fetch: fetchEvaluations } = useEvaluationList();
  const { fetch: fetchCommissions } = useCommissionItemList();

  const [partner, setPartner] = useState<ChannelPartnerDto | null>(null);
  const [contacts, setContacts] = useState<readonly ChannelContactDto[]>([]);
  const [activities, setActivities] = useState<readonly BusinessDevActivityDto[]>([]);
  const [costs, setCosts] = useState<readonly ChannelCostDto[]>([]);
  const [evaluations, setEvaluations] = useState<readonly ChannelEffectEvaluationDto[]>([]);
  const [commissions, setCommissions] = useState<readonly ChannelCommissionItemDto[]>([]);

  const [coopDialogOpen, setCoopDialogOpen] = useState(false);
  const [addContactOpen, setAddContactOpen] = useState(false);
  const [addActivityOpen, setAddActivityOpen] = useState(false);
  const [addCostOpen, setAddCostOpen] = useState(false);
  const [addEvalOpen, setAddEvalOpen] = useState(false);

  const canRead = hasPermission(permissions, CHANNEL_ROI_PERMISSIONS.PARTNER_READ);
  const canUpdateStatus = hasPermission(permissions, CHANNEL_ROI_PERMISSIONS.PARTNER_UPDATE);
  const canAddContact = hasPermission(permissions, CHANNEL_ROI_PERMISSIONS.CONTACT_CREATE);
  const canDeleteContact = hasPermission(permissions, CHANNEL_ROI_PERMISSIONS.CONTACT_DELETE);
  const canAddActivity = hasPermission(permissions, CHANNEL_ROI_PERMISSIONS.ACTIVITY_CREATE);
  const canAddCost = hasPermission(permissions, CHANNEL_ROI_PERMISSIONS.COST_CREATE);
  const canAddEvaluation = hasPermission(permissions, CHANNEL_ROI_PERMISSIONS.EVALUATION_CREATE);

  const loadPartner = useCallback(async () => {
    if (!partnerId || !canRead) return;
    const result = await fetchPartner(partnerId);
    if (result) setPartner(result);
  }, [fetchPartner, partnerId, canRead]);

  const loadContacts = useCallback(async () => {
    if (!partnerId || !canRead) return;
    const result = await fetchContacts({ channelPartnerId: partnerId, limit: 50 });
    if (result) setContacts(result);
  }, [fetchContacts, partnerId, canRead]);

  const loadActivities = useCallback(async () => {
    if (!partnerId || !canRead) return;
    const result = await fetchActivities({ channelPartnerId: partnerId, limit: 50 });
    if (result) setActivities(result);
  }, [fetchActivities, partnerId, canRead]);

  const loadCosts = useCallback(async () => {
    if (!partnerId || !canRead) return;
    const result = await fetchCosts({ channelPartnerId: partnerId, limit: 50 });
    if (result) setCosts(result);
  }, [fetchCosts, partnerId, canRead]);

  const loadEvaluations = useCallback(async () => {
    if (!partnerId || !canRead) return;
    const result = await fetchEvaluations({ channelPartnerId: partnerId, limit: 50 });
    if (result) setEvaluations(result);
  }, [fetchEvaluations, partnerId, canRead]);

  const loadCommissions = useCallback(async () => {
    if (!partnerId || !canRead) return;
    const result = await fetchCommissions({ channelPartnerId: partnerId, limit: 50 });
    if (result) setCommissions(result);
  }, [fetchCommissions, partnerId, canRead]);

  useEffect(() => {
    void loadPartner();
    void loadContacts();
    void loadActivities();
    void loadCosts();
    void loadEvaluations();
    void loadCommissions();
  }, [
    loadPartner,
    loadContacts,
    loadActivities,
    loadCosts,
    loadEvaluations,
    loadCommissions,
  ]);

  async function handleDeleteContact(id: string) {
    const ok = await removeContact(id);
    if (ok) {
      toast.success('联系人已删除');
      void loadContacts();
    }
  }

  // ─── Decimal-safe cost sum ─────────────────────────────────────────────────
  // Use sumDecimalStrings to avoid IEEE-754 floating-point rounding errors.
  const totalCostFormatted = formatMoney(sumDecimalStrings(costs.map((c) => c.amount)));

  if (!canRead) {
    return <AccessDenied resource="渠道详情" />;
  }

  if (partnerLoading && !partner) {
    return <div className="p-8 text-center text-sm text-muted-foreground">加载中…</div>;
  }

  if (!partner) {
    return (
      <div className="p-8 text-center text-sm text-muted-foreground">
        渠道不存在或无权访问。
        <button
          className="ml-2 text-primary hover:underline"
          onClick={() => navigate('/channel-roi')}
        >
          返回列表
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button
            className="mb-1 text-sm text-muted-foreground hover:underline"
            onClick={() => navigate('/channel-roi')}
          >
            ← 返回渠道列表
          </button>
          <h2 className="text-xl font-semibold">{partner.name}</h2>
        </div>
        {canUpdateStatus && (
          <Button variant="outline" size="sm" onClick={() => setCoopDialogOpen(true)}>
            合作状态转换
          </Button>
        )}
      </div>

      {/* Basic Info */}
      <Card className="p-4">
        <h3 className="mb-3 font-medium">基本信息</h3>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm md:grid-cols-3">
          <dt className="text-muted-foreground">合作状态</dt>
          <dd>
            <CoopStatusBadge status={partner.cooperationStatus} />
          </dd>
          <dt className="text-muted-foreground">合作类型</dt>
          <dd>{partner.cooperationType ?? '—'}</dd>
          <dt className="text-muted-foreground">佣金率</dt>
          <dd>{partner.commissionRate ? `${partner.commissionRate}` : '—'}</dd>
          <dt className="text-muted-foreground">合同开始</dt>
          <dd>
            {partner.contractStartDate
              ? new Date(partner.contractStartDate).toLocaleDateString('zh-CN')
              : '—'}
          </dd>
          <dt className="text-muted-foreground">合同到期</dt>
          <dd>
            {partner.contractEndDate
              ? new Date(partner.contractEndDate).toLocaleDateString('zh-CN')
              : '—'}
          </dd>
          <dt className="text-muted-foreground">备注</dt>
          <dd className="col-span-2">{partner.notes ?? '—'}</dd>
          <dt className="text-muted-foreground">版本</dt>
          <dd>{partner.version}</dd>
        </dl>
      </Card>

      {/* ROI Summary */}
      <Card className="p-4">
        <h3 className="mb-3 font-medium">ROI 投入汇总</h3>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-2xl font-bold text-primary">{totalCostFormatted}</p>
            <p className="text-xs text-muted-foreground">总成本投入</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-green-600">
              {evaluations.length > 0
                ? formatMoney(evaluations[0].totalRevenue)
                : '—'}
            </p>
            <p className="text-xs text-muted-foreground">最近期收入</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-blue-600">
              {evaluations.length > 0
                ? formatRatio(evaluations[0].roiRatio ?? null, 1)
                : '—'}
            </p>
            <p className="text-xs text-muted-foreground">最近期 ROI</p>
          </div>
        </div>
      </Card>

      {/* Contacts */}
      <Card className="p-4">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-medium">联系人</h3>
          {canAddContact && (
            <Button size="sm" onClick={() => setAddContactOpen(true)}>
              添加联系人
            </Button>
          )}
        </div>
        {contacts.length === 0 ? (
          <p className="text-sm text-muted-foreground">暂无联系人</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/40">
              <tr>
                <th className="px-3 py-2 text-left font-medium">姓名</th>
                <th className="px-3 py-2 text-left font-medium">职位</th>
                <th className="px-3 py-2 text-left font-medium">手机</th>
                <th className="px-3 py-2 text-left font-medium">邮箱</th>
                <th className="px-3 py-2 text-left font-medium">主要</th>
                {canDeleteContact && (
                  <th className="px-3 py-2 text-left font-medium">操作</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y">
              {contacts.map((c) => (
                <tr key={c.id} className="hover:bg-muted/30">
                  <td className="px-3 py-2 font-medium">{c.name}</td>
                  <td className="px-3 py-2 text-muted-foreground">{c.title ?? '—'}</td>
                  <td className="px-3 py-2 text-muted-foreground">{c.phone ?? '—'}</td>
                  <td className="px-3 py-2 text-muted-foreground">{c.email ?? '—'}</td>
                  <td className="px-3 py-2">
                    {c.isPrimary ? (
                      <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700">
                        主要
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  {canDeleteContact && (
                    <td className="px-3 py-2">
                      <button
                        className="text-xs text-destructive hover:underline"
                        onClick={() => {
                          void handleDeleteContact(c.id);
                        }}
                      >
                        删除
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Activities */}
      <Card className="p-4">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-medium">业务拓展活动</h3>
          {canAddActivity && (
            <Button size="sm" onClick={() => setAddActivityOpen(true)}>
              新建活动
            </Button>
          )}
        </div>
        {activities.length === 0 ? (
          <p className="text-sm text-muted-foreground">暂无活动记录</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/40">
              <tr>
                <th className="px-3 py-2 text-left font-medium">活动名称</th>
                <th className="px-3 py-2 text-left font-medium">类型</th>
                <th className="px-3 py-2 text-left font-medium">状态</th>
                <th className="px-3 py-2 text-left font-medium">开始日期</th>
                <th className="px-3 py-2 text-left font-medium">预算</th>
                <th className="px-3 py-2 text-left font-medium">实际</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {activities.map((a) => (
                <tr key={a.id} className="hover:bg-muted/30">
                  <td className="px-3 py-2 font-medium">{a.name}</td>
                  <td className="px-3 py-2 text-muted-foreground">{a.activityType}</td>
                  <td className="px-3 py-2">
                    <span className="rounded-full bg-muted px-2 py-0.5 text-xs">
                      {ACTIVITY_STATUS_LABELS[a.status as ActivityStatus] ?? a.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{a.startDate}</td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {a.budgetAmount ? formatMoney(a.budgetAmount) : '—'}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {a.actualAmount ? formatMoney(a.actualAmount) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Costs */}
      <Card className="p-4">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-medium">成本记录</h3>
          {canAddCost && (
            <Button size="sm" onClick={() => setAddCostOpen(true)}>
              添加成本
            </Button>
          )}
        </div>
        {costs.length === 0 ? (
          <p className="text-sm text-muted-foreground">暂无成本记录</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/40">
              <tr>
                <th className="px-3 py-2 text-left font-medium">类型</th>
                <th className="px-3 py-2 text-left font-medium">金额</th>
                <th className="px-3 py-2 text-left font-medium">日期</th>
                <th className="px-3 py-2 text-left font-medium">描述</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {costs.map((c) => (
                <tr key={c.id} className="hover:bg-muted/30">
                  <td className="px-3 py-2">{c.costType}</td>
                  <td className="px-3 py-2 font-medium text-destructive">
                    {formatMoney(c.amount)}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{c.costDate}</td>
                  <td className="px-3 py-2 text-muted-foreground">{c.description ?? '—'}</td>
                </tr>
              ))}
              <tr className="border-t bg-muted/20 font-medium">
                <td className="px-3 py-2">合计</td>
                <td className="px-3 py-2 text-destructive">{totalCostFormatted}</td>
                <td colSpan={2} />
              </tr>
            </tbody>
          </table>
        )}
      </Card>

      {/* Evaluations */}
      <Card className="p-4">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-medium">效果评价</h3>
          {canAddEvaluation && (
            <Button size="sm" onClick={() => setAddEvalOpen(true)}>
              新建评价
            </Button>
          )}
        </div>
        {evaluations.length === 0 ? (
          <p className="text-sm text-muted-foreground">暂无效果评价</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/40">
              <tr>
                <th className="px-3 py-2 text-left font-medium">周期</th>
                <th className="px-3 py-2 text-left font-medium">线索数</th>
                <th className="px-3 py-2 text-left font-medium">转化数</th>
                <th className="px-3 py-2 text-left font-medium">总收入</th>
                <th className="px-3 py-2 text-left font-medium">总成本</th>
                <th className="px-3 py-2 text-left font-medium">ROI</th>
                <th className="px-3 py-2 text-left font-medium">状态</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {evaluations.map((e) => (
                <tr key={e.id} className="hover:bg-muted/30">
                  <td className="px-3 py-2 text-muted-foreground">
                    {e.periodStart} ~ {e.periodEnd}
                  </td>
                  <td className="px-3 py-2">{e.totalLeads}</td>
                  <td className="px-3 py-2">{e.convertedLeads}</td>
                  <td className="px-3 py-2 text-green-600">{formatMoney(e.totalRevenue)}</td>
                  <td className="px-3 py-2 text-destructive">{formatMoney(e.totalCost)}</td>
                  <td className="px-3 py-2 font-medium text-blue-600">
                    {formatRatio(e.roiRatio ?? null, 1)}
                  </td>
                  <td className="px-3 py-2">
                    <span className="rounded-full bg-muted px-2 py-0.5 text-xs">
                      {EVALUATION_STATUS_LABELS[e.status as EvaluationStatus] ?? e.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Commission Items */}
      <Card className="p-4">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-medium">分成明细</h3>
          <p className="text-xs text-muted-foreground">新建分成请通过订单结算流程触发</p>
        </div>
        {commissions.length === 0 ? (
          <p className="text-sm text-muted-foreground">暂无分成明细</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/40">
              <tr>
                <th className="px-3 py-2 text-left font-medium">类型</th>
                <th className="px-3 py-2 text-left font-medium">基数</th>
                <th className="px-3 py-2 text-left font-medium">费率</th>
                <th className="px-3 py-2 text-left font-medium">分成金额</th>
                <th className="px-3 py-2 text-left font-medium">状态</th>
                <th className="px-3 py-2 text-left font-medium">结算周期</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {commissions.map((c) => (
                <tr key={c.id} className="hover:bg-muted/30">
                  <td className="px-3 py-2">{c.commissionType}</td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {formatMoney(c.baseAmount)}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {formatRatio(c.commissionRate, 2)}
                  </td>
                  <td className="px-3 py-2 font-medium">{formatMoney(c.commissionAmount)}</td>
                  <td className="px-3 py-2">
                    <span className="rounded-full bg-muted px-2 py-0.5 text-xs">
                      {COMMISSION_STATUS_LABELS[c.status as CommissionStatus] ?? c.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {c.settlementPeriod ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Dialogs — key forces remount on partner change */}
      {coopDialogOpen && (
        <CoopTransitionDialog
          key={`coop-${partner.id}-${partner.version}`}
          partner={partner}
          open={coopDialogOpen}
          onOpenChange={setCoopDialogOpen}
          onTransitioned={() => {
            void loadPartner();
          }}
        />
      )}
      {addContactOpen && (
        <AddContactDialog
          key={`add-contact-${partner.id}`}
          channelPartnerId={partner.id}
          open={addContactOpen}
          onOpenChange={setAddContactOpen}
          onCreated={() => {
            void loadContacts();
          }}
        />
      )}
      {addActivityOpen && (
        <AddActivityDialog
          key={`add-activity-${partner.id}`}
          channelPartnerId={partner.id}
          open={addActivityOpen}
          onOpenChange={setAddActivityOpen}
          onCreated={() => {
            void loadActivities();
          }}
        />
      )}
      {addCostOpen && (
        <AddCostDialog
          key={`add-cost-${partner.id}`}
          channelPartnerId={partner.id}
          open={addCostOpen}
          onOpenChange={setAddCostOpen}
          onCreated={() => {
            void loadCosts();
          }}
        />
      )}
      {addEvalOpen && (
        <AddEvaluationDialog
          key={`add-eval-${partner.id}`}
          channelPartnerId={partner.id}
          open={addEvalOpen}
          onOpenChange={setAddEvalOpen}
          onCreated={() => {
            void loadEvaluations();
          }}
        />
      )}
    </div>
  );
}
